<template>
  <svg
    class="save-spinner-icon"
    viewBox="0 0 20 20"
    role="status"
    aria-label="Submitting…"
  >
    <circle
      class="save-spinner-ring"
      cx="10"
      cy="10"
      r="8.15"
    />
    <line
      v-for="segmentIndex in segmentIndices"
      :key="segmentIndex"
      class="save-spinner-segment"
      :class="{ 'has-secondary-visit': segmentHasSecondaryVisit[segmentIndex] }"
      x1="10"
      y1="4.85"
      x2="10"
      y2="7.15"
      :style="segmentStyles[segmentIndex]"
      :transform="`rotate(${segmentIndex * segmentAngle} 10 10)`"
    />
  </svg>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  getSpinnerSegmentAnimationDelay,
  spinnerSegmentHasSecondaryVisit,
  type SpinnerDirection,
} from "./spinnerTiming";

export type { SpinnerDirection } from "./spinnerTiming";
const props = withDefaults(defineProps<{
  segments?: number,
  direction?: SpinnerDirection,
  speed?: number,
}>(), {
  segments: 12,
  direction: "clockwise",
  speed: 1,
});

const safeSegmentCount = computed(() => Math.max(1, props.segments));
const safeSpeed = computed(() => (
  Number.isFinite(props.speed) && props.speed > 0
    ? props.speed
    : 1
));
const segmentIndices = computed(() =>
  Array.from({ length: safeSegmentCount.value }, (_, index) => index),
);
const segmentAngle = computed(() => 360 / safeSegmentCount.value);
const segmentDuration = computed(() => `${1 / safeSpeed.value}s`);
const segmentHasSecondaryVisit = computed(() =>
  segmentIndices.value.map((segmentIndex) =>
    spinnerSegmentHasSecondaryVisit(props.direction, safeSegmentCount.value, segmentIndex)
  ),
);

const segmentStyles = computed(() =>
  segmentIndices.value.map((segmentIndex) => ({
    "--segment-primary-delay": `${getSpinnerSegmentAnimationDelay(
      props.direction,
      safeSegmentCount.value,
      segmentIndex,
      0,
    )}s`,
    "--segment-secondary-delay": `${getSpinnerSegmentAnimationDelay(
      props.direction,
      safeSegmentCount.value,
      segmentIndex,
      1,
    )}s`,
    "--segment-duration": segmentDuration.value,
  })),
);
</script>

<style scoped>
@property --segment-opacity-primary {
  syntax: "<number>";
  inherits: false;
  initial-value: 0.18;
}

@property --segment-opacity-secondary {
  syntax: "<number>";
  inherits: false;
  initial-value: 0.18;
}

svg {
  width: 1em;
  height: 1em;
}

.save-spinner-icon {
  width: 4em;
  height: 4em;
  overflow: visible;
  color: white;
}

.save-spinner-ring {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
}

.save-spinner-segment {
  --segment-opacity-primary: 0.18;
  --segment-opacity-secondary: 0.18;
  stroke: currentColor;
  stroke-width: 1.15;
  stroke-linecap: round;
  opacity: max(var(--segment-opacity-primary), var(--segment-opacity-secondary));
  animation-name: saveSpinnerFadePrimary;
  animation-duration: var(--segment-duration, 1s);
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  animation-fill-mode: both;
  animation-delay: var(--segment-primary-delay, 0s);
}

.save-spinner-segment.has-secondary-visit {
  animation-name: saveSpinnerFadePrimary, saveSpinnerFadeSecondary;
  animation-duration: var(--segment-duration, 1s), var(--segment-duration, 1s);
  animation-timing-function: linear, linear;
  animation-iteration-count: infinite, infinite;
  animation-fill-mode: both, both;
  animation-delay: var(--segment-primary-delay, 0s), var(--segment-secondary-delay, 0s);
}

@keyframes saveSpinnerFadePrimary {
  0% {
    --segment-opacity-primary: 1;
  }
  70% {
    --segment-opacity-primary: 0.18;
  }
  100% {
    --segment-opacity-primary: 0.18;
  }
}

@keyframes saveSpinnerFadeSecondary {
  0% {
    --segment-opacity-secondary: 1;
  }
  70% {
    --segment-opacity-secondary: 0.18;
  }
  100% {
    --segment-opacity-secondary: 0.18;
  }
}
</style>
